import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowRight } from "lucide-react";

// Care tiers with hourly rate ranges (CAD)
const CARE_TIERS = [
  {
    id: "companion",
    label: "Companionship & Light Help",
    blurb: "Visits, conversation, light housekeeping, meals.",
    rate: [30, 38],
  },
  {
    id: "personal",
    label: "Personal & Senior Care",
    blurb: "Hygiene, mobility, medication reminders, daily routines.",
    rate: [34, 44],
  },
  {
    id: "specialised",
    label: "Specialised Care",
    blurb: "Dementia, post-surgery recovery, complex needs.",
    rate: [40, 52],
  },
];

const HOURS = [
  { id: "2-4", label: "2 to 4 hrs/day", value: 3 },
  { id: "4-8", label: "4 to 8 hrs/day", value: 6 },
  { id: "8-12", label: "8 to 12 hrs/day", value: 10 },
  { id: "24", label: "24/7 live-in", value: 24 },
];

const DAYS = [
  { id: "1-2", label: "1 to 2 days/wk", value: 1.5 },
  { id: "3-4", label: "3 to 4 days/wk", value: 3.5 },
  { id: "5-7", label: "5 to 7 days/wk", value: 6 },
];

const fmt = (n) => `$${Math.round(n).toLocaleString("en-CA")}`;

export default function QuickQuote() {
  const [tier, setTier] = useState(CARE_TIERS[1]);
  const [hours, setHours] = useState(HOURS[1]);
  const [days, setDays] = useState(DAYS[1]);

  const estimate = useMemo(() => {
    const weeklyHours = hours.value * days.value;
    if (hours.id === "24") {
      // Live-in flat daily rate (CAD)
      const dailyLow = 280 + (tier.rate[0] - 34) * 4;
      const dailyHigh = 360 + (tier.rate[1] - 44) * 4;
      const d = days.value === 6 ? 7 : days.value;
      return {
        hours: "24/7",
        low: dailyLow * d,
        high: dailyHigh * d,
        note: "Flat live-in daily rate",
      };
    }
    return {
      hours: weeklyHours,
      low: weeklyHours * tier.rate[0],
      high: weeklyHours * tier.rate[1],
      note: `~${weeklyHours} hrs/week`,
    };
  }, [tier, hours, days]);

  return (
    <section
      id="quote"
      className="py-20 md:py-32 bg-[#F8FAFC]"
      data-testid="quick-quote-section"
    >
      <div className="container-pad mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#2A9D8F] mb-5">
              Care Plan Quick-Quote
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0F2942] leading-[1.1]" data-testid="quote-heading">
              An estimate, in three quiet questions.
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed max-w-md">
              Costs vary with care needs and schedule. Use the slider below for
              a transparent ballpark, then book a free consultation for a
              personalised plan.
            </p>

            <div className="mt-8 hidden lg:flex items-start gap-4 rounded-2xl bg-[#EEF4F8] p-5 border border-[#1E5F8C]/5">
              <div className="h-10 w-10 rounded-xl bg-[#DCEAF2] flex items-center justify-center shrink-0">
                <Calculator size={18} className="text-[#1E5F8C]" />
              </div>
              <p className="text-sm text-[#475569] leading-relaxed">
                Estimates are CAD ranges based on typical GTA rates. Final
                pricing depends on caregiver match, complexity, and schedule.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 bg-[#EEF4F8] rounded-3xl p-6 md:p-10 border border-[#1E5F8C]/10 soft-shadow"
            data-testid="quick-quote-card"
          >
            <Step n={1} label="Type of care">
              <div className="grid sm:grid-cols-3 gap-3" data-testid="quote-tier-group">
                {CARE_TIERS.map((t) => (
                  <ChoiceCard
                    key={t.id}
                    active={tier.id === t.id}
                    onClick={() => setTier(t)}
                    title={t.label}
                    sub={t.blurb}
                    testId={`quote-tier-${t.id}`}
                  />
                ))}
              </div>
            </Step>

            <Step n={2} label="Hours per day">
              <div className="flex flex-wrap gap-2.5" data-testid="quote-hours-group">
                {HOURS.map((h) => (
                  <Pill
                    key={h.id}
                    active={hours.id === h.id}
                    onClick={() => setHours(h)}
                    label={h.label}
                    testId={`quote-hours-${h.id}`}
                  />
                ))}
              </div>
            </Step>

            <Step n={3} label="Days per week">
              <div className="flex flex-wrap gap-2.5" data-testid="quote-days-group">
                {DAYS.map((d) => (
                  <Pill
                    key={d.id}
                    active={days.id === d.id}
                    onClick={() => setDays(d)}
                    label={d.label}
                    testId={`quote-days-${d.id}`}
                  />
                ))}
              </div>
            </Step>

            <div className="mt-8 rounded-2xl bg-[#1E5F8C] text-[#F8FAFC] p-6 md:p-7" data-testid="quote-estimate">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2A9D8F]">
                Estimated weekly investment
              </p>
              <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="quote-low">
                  {fmt(estimate.low)}
                </span>
                <span className="font-serif text-2xl md:text-3xl text-[#F8FAFC]/60">to</span>
                <span className="font-serif text-4xl md:text-5xl tracking-tight" data-testid="quote-high">
                  {fmt(estimate.high)}
                </span>
                <span className="text-sm text-[#F8FAFC]/70 ml-1">/week</span>
              </div>
              <p className="mt-2 text-sm text-[#F8FAFC]/70">{estimate.note}</p>

              <a
                href="#booking"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#F8FAFC] text-[#0F2942] hover:bg-[#DCEAF2] px-6 py-3.5 text-sm font-medium transition-colors"
                data-testid="quote-cta-book"
              >
                Get a personalised plan <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Step({ n, label, children }) {
  return (
    <div className="mb-7 last:mb-0">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-7 w-7 rounded-full bg-[#1E5F8C] text-[#F8FAFC] flex items-center justify-center text-xs font-semibold">
          {n}
        </span>
        <span className="font-serif text-lg md:text-xl text-[#0F2942]">{label}</span>
      </div>
      {children}
    </div>
  );
}

function ChoiceCard({ active, onClick, title, sub, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active}
      className={`text-left rounded-2xl p-4 border transition-all ${
        active
          ? "bg-[#F8FAFC] border-[#1E5F8C] shadow-[0_4px_18px_rgba(43,76,62,0.08)]"
          : "bg-[#F8FAFC]/60 border-[#1E5F8C]/10 hover:border-[#1E5F8C]/30"
      }`}
    >
      <p className={`text-sm font-semibold ${active ? "text-[#1E5F8C]" : "text-[#0F2942]"}`}>
        {title}
      </p>
      <p className="mt-1 text-xs text-[#475569] leading-relaxed">{sub}</p>
    </button>
  );
}

function Pill({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active}
      className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all ${
        active
          ? "bg-[#1E5F8C] text-[#F8FAFC] border-[#1E5F8C]"
          : "bg-[#F8FAFC] text-[#0F2942] border-[#1E5F8C]/15 hover:border-[#1E5F8C]/40"
      }`}
    >
      {label}
    </button>
  );
}
